// Vista por área para jefaturas: semáforo del área, carga por persona,
// tickets abiertos del área y lo que el área pidió a otras.
import * as React from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { EsqueletoTabla, PuntoNaranja, Select, SelectContenido, SelectDisparador, SelectOpcion, SelectValor, Semaforo, TituloPagina, Vacio } from "@iavmx/ui";
import { usePersona, useVistaArea, type Periodo } from "./datos";
import { TablaTickets } from "./Bandeja";
import { Cifra, ErrorCarga, PorConfirmar, Segmento, Seccion } from "./comunes";
import { fechaCorta, pct } from "./formato";

export function MiArea() {
  const yo = usePersona();
  const navegar = useNavigate();
  const busqueda = useSearch({ strict: false }) as { area?: string };
  const [periodo, setPeriodo] = React.useState<Periodo>("semana");
  const areasMias = yo.data?.persona.areas ?? [];
  const clave = busqueda.area ?? yo.data?.persona.areaPrincipal ?? areasMias[0] ?? null;
  const q = useVistaArea(clave, periodo);
  const d = q.data;
  const ahora = d ? new Date(d.ahora) : new Date();
  const puedeElegir = yo.data?.veTodo || areasMias.length > 1;
  const opcionesArea = yo.data?.veTodo ? null : areasMias;
  const setArea = (a: string) => void navegar({ to: "/area", search: { area: a } });
  return (
    <>
      <TituloPagina
        descripcion={d ? `${d.jefe ? `Jefatura: ${d.jefe.nombre}` : "Sin jefatura en el directorio"} · desde el ${fechaCorta(d.periodo.desde)}` : "Cargando el área…"}
        accion={
          <div className="flex flex-wrap items-center gap-3">
            {puedeElegir && (
              <Select value={clave ?? ""} onValueChange={setArea}>
                <SelectDisparador className="w-56 text-base md:text-sm" aria-label="Área">
                  <SelectValor placeholder="Elige el área" />
                </SelectDisparador>
                <SelectContenido>
                  {(opcionesArea ?? []).map((a) => (
                    <SelectOpcion key={a} value={a}>
                      {a}
                    </SelectOpcion>
                  ))}
                  {yo.data?.veTodo && <OpcionesTodasLasAreas />}
                </SelectContenido>
              </Select>
            )}
            <Segmento etiqueta="Periodo" valor={periodo} onChange={setPeriodo} opciones={[{ valor: "hoy", texto: "Hoy" }, { valor: "semana", texto: "Semana" }, { valor: "mes", texto: "Mes" }]} />
          </div>
        }
      >
        {d ? d.area.nombre : "Mi área"}
        <PuntoNaranja />
      </TituloPagina>
      {!clave ? (
        <Vacio titulo="No tienes área en el directorio" texto="Pide a tu jefatura que te dé de alta en un área para ver esta vista." />
      ) : q.isLoading ? (
        <EsqueletoTabla filas={6} />
      ) : q.error || !d ? (
        <ErrorCarga error={q.error ?? new Error("Sin datos")} />
      ) : (
        <div className="flex flex-col gap-8">
          <div className="grid grid-cols-2 gap-x-6 border-y border-t-tinta border-b-linea lg:grid-cols-5">
            <div className="flex flex-col gap-1.5 py-4">
              <span className="font-ui text-[11px] font-semibold uppercase tracking-wide text-tinta-tenue">Semáforo del área</span>
              <span className="pt-1">{d.semaforo ? <Semaforo color={d.semaforo}>{d.semaforo === "ambar" ? "Ámbar" : d.semaforo === "rojo" ? "Rojo" : "Verde"}</Semaforo> : <Semaforo color="gris">Sin entregas</Semaforo>}</span>
            </div>
            <Cifra etiqueta="Abiertos" valor={d.cifras.abiertos} nota={`${d.cifras.porAsignar} por asignar`} />
            <Cifra etiqueta="Vencidos" valor={d.cifras.vencidos} tono={d.cifras.vencidos > 0 ? "rojo" : undefined} />
            <Cifra etiqueta="Entregas" valor={d.cifras.entregas} nota="en el periodo" />
            <Cifra
              etiqueta="Cumplimiento"
              valor={d.cifras.cumplimiento ?? "—"}
              sufijo={d.cifras.cumplimiento === null ? undefined : "%"}
              tono={d.cifras.cumplimiento === null ? undefined : d.cifras.cumplimiento >= d.meta ? "verde" : "ambar"}
              nota={
                <>
                  meta {d.meta} % <PorConfirmar que="Meta de cumplimiento" />
                </>
              }
            />
          </div>

          <Seccion titulo="Personas del área" extra="Carga abierta, vencidos y cumplimiento en el periodo">
            {d.personas.length === 0 ? (
              <p className="font-ui text-sm text-tinta-tenue">Nadie está dado de alta en esta área.</p>
            ) : (
              <>
              <ul className="flex flex-col divide-y divide-linea border-t border-tinta md:hidden">
                {d.personas.map((p) => (
                  <li key={p.persona.id} className="flex flex-col gap-1 py-3 font-ui text-sm">
                    <span className="font-semibold">{p.persona.nombre}</span>
                    <span className="text-tinta-2">
                      {p.abiertos} {p.abiertos === 1 ? "abierto" : "abiertos"} ·{" "}
                      <span className={p.vencidos > 0 ? "font-semibold text-rojo" : ""}>
                        {p.vencidos} {p.vencidos === 1 ? "vencido" : "vencidos"}
                      </span>{" "}
                      · {p.entregas} {p.entregas === 1 ? "entrega" : "entregas"} · cumplimiento {pct(p.cumplimiento)}
                    </span>
                    {p.proximo && <span className="text-tinta-tenue">Próximo: {p.proximo.titulo} · {fechaCorta(p.proximo.venceEn)}</span>}
                  </li>
                ))}
              </ul>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[620px] font-ui text-sm">
                  <thead>
                    <tr className="border-b border-tinta text-left text-[11px] font-semibold uppercase tracking-wide text-tinta-tenue">
                      <th className="py-2 pr-3 font-semibold">Persona</th>
                      <th className="py-2 pr-3 text-right font-semibold">Abiertos</th>
                      <th className="py-2 pr-3 text-right font-semibold">Vencidos</th>
                      <th className="py-2 pr-3 text-right font-semibold">Entregas</th>
                      <th className="py-2 pr-3 text-right font-semibold">Cumplimiento</th>
                      <th className="py-2 font-semibold">Próximo vencimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.personas.map((p) => (
                      <tr key={p.persona.id} className="border-b border-linea">
                        <td className="py-3 pr-3">
                          <span className="font-semibold">{p.persona.nombre}</span>
                          {p.persona.puesto && <span className="text-tinta-tenue"> · {p.persona.puesto}</span>}
                        </td>
                        <td className="py-3 pr-3 text-right tabular-nums">{p.abiertos}</td>
                        <td className={`py-3 pr-3 text-right tabular-nums ${p.vencidos > 0 ? "font-semibold text-rojo" : ""}`}>{p.vencidos}</td>
                        <td className="py-3 pr-3 text-right tabular-nums">{p.entregas}</td>
                        <td className="py-3 pr-3 text-right tabular-nums">{pct(p.cumplimiento)}</td>
                        <td className="py-3 text-tinta-2">{p.proximo ? `${p.proximo.titulo} · ${fechaCorta(p.proximo.venceEn)}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </Seccion>

          <Seccion titulo="Tickets abiertos del área" extra={`${d.tickets.length} · vencidos primero`}>
            {d.tickets.length === 0 ? <p className="font-ui text-sm text-tinta-tenue">Nada abierto asignado a esta área.</p> : <TablaTickets tickets={d.tickets} ahora={ahora} />}
          </Seccion>

          <Seccion titulo="Lo que el área pidió a otras" extra={`${d.pedidos.length}`}>
            {d.pedidos.length === 0 ? <p className="font-ui text-sm text-tinta-tenue">Ninguna solicitud abierta hacia otras áreas.</p> : <TablaTickets tickets={d.pedidos} ahora={ahora} />}
          </Seccion>
        </div>
      )}
    </>
  );
}

function OpcionesTodasLasAreas() {
  const yo = usePersona();
  const { data } = useAreas();
  return (
    <>
      {data?.map((a) => (
        <SelectOpcion key={a.clave} value={a.clave}>
          {a.nombre}
          {yo.data?.persona.areas.includes(a.clave) ? " · mía" : ""}
        </SelectOpcion>
      ))}
    </>
  );
}

import { useCatalogo } from "./datos";
function useAreas() {
  const c = useCatalogo();
  return { data: c.data?.areas };
}
